import type { SessionScope } from '../http';
import { isGlobalAdmin } from './access';
import type { Identity, OrgNodeRef, OrgNodeTree } from './types';

/**
 * Which org node the user is looking at. Ported from portal-ui-lib's `ScopeState` and
 * `ScopePersistenceService`.
 *
 * A plain store, not React state: the session client reads it synchronously on every request
 * (`?orgNodeId=`), outside any component. React reads it through `useSyncExternalStore`.
 *
 * - The root is where the user starts: the tree's root for a GLOBAL admin, the user's
 *   `originNode` for everyone else.
 * - `stack` is the breadcrumb below the root; `current` is its last entry, or the root.
 * - `viewMode` is a GLOBAL admin's choice between `admin` (bypasses on) and `scoped` (see the
 *   node as its users do); anyone else is always `admin`, and cannot change it.
 * - The selection survives a reload: it is written to localStorage under `ui.scope.<userId>`
 *   (ids only), and restored once per session when the tree has loaded — only if every id still
 *   resolves in the tree, so a node the user lost access to is dropped, never widened to.
 */

export type ScopeViewMode = 'admin' | 'scoped';

export interface ScopeSnapshot {
  userId: string | null;
  root: OrgNodeRef | null;
  stack: readonly OrgNodeRef[];
  /** `stack`'s last entry, or the root. Null before the scope is known. */
  current: OrgNodeRef | null;
  viewMode: ScopeViewMode;
  /** Only GLOBAL admins may switch view mode. */
  canToggleViewMode: boolean;
}

export interface ScopeStore {
  getSnapshot(): ScopeSnapshot;
  subscribe(listener: () => void): () => void;
  /** What the session client narrows requests to. */
  requestScope(): SessionScope | null;
  /**
   * Bring the scope in line with the session: call whenever the identity or the tree changes.
   * A different user starts over; the root is set as soon as it is known; the persisted
   * selection is restored once, after the tree has loaded.
   */
  sync(identity: Identity, tree: OrgNodeTree | null): void;
  /** Forget the selection in memory (a different user, or no session). */
  clear(): void;
  /** Logged out: forget the selection and every persisted one in this browser, as Angular did. */
  forgetAll(): void;
  /** Go one level down. */
  push(node: OrgNodeRef): void;
  /** Go one level up (no-op at the root). */
  pop(): void;
  /** Jump straight to a node, replacing the breadcrumb. */
  resetTo(node: OrgNodeRef): void;
  /** Ignored for anyone who is not a GLOBAL admin. */
  setViewMode(mode: ScopeViewMode): void;
}

export const SCOPE_STORAGE_PREFIX = 'ui.scope.';

interface PersistedScope {
  orgNodeId: string;
  stackIds: string[];
  viewMode: ScopeViewMode;
}

function readPersisted(storage: Storage, userId: string): PersistedScope | null {
  try {
    const raw = storage.getItem(`${SCOPE_STORAGE_PREFIX}${userId}`);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PersistedScope> | null;
    if (!parsed || typeof parsed.orgNodeId !== 'string' || !parsed.orgNodeId) {
      return null;
    }
    return {
      orgNodeId: parsed.orgNodeId,
      stackIds: Array.isArray(parsed.stackIds)
        ? parsed.stackIds.filter((id): id is string => typeof id === 'string')
        : [],
      viewMode: parsed.viewMode === 'scoped' ? 'scoped' : 'admin',
    };
  } catch {
    return null;
  }
}

/** Drop every persisted selection — on logout, where the user id may already be gone. */
export function removePersistedScopes(storage: Storage = localStorage): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(SCOPE_STORAGE_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach(key => storage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was persisted either.
  }
}

const EMPTY: ScopeSnapshot = {
  userId: null,
  root: null,
  stack: [],
  current: null,
  viewMode: 'admin',
  canToggleViewMode: false,
};

const toRef = (node: OrgNodeRef): OrgNodeRef => ({ id: node.id, type: node.type, name: node.name });

export function createScopeStore(storage: Storage = localStorage): ScopeStore {
  let snapshot = EMPTY;
  let restoreAttempted = false;
  const listeners = new Set<() => void>();

  function set(next: Omit<ScopeSnapshot, 'current'>): void {
    const unchanged =
      next.userId === snapshot.userId &&
      next.root?.id === snapshot.root?.id &&
      next.viewMode === snapshot.viewMode &&
      next.canToggleViewMode === snapshot.canToggleViewMode &&
      next.stack.length === snapshot.stack.length &&
      next.stack.every((node, i) => node.id === snapshot.stack[i]?.id);
    if (unchanged) {
      return;
    }
    snapshot = { ...next, current: next.stack.at(-1) ?? next.root };
    persist();
    listeners.forEach(listener => listener());
  }

  // Nothing is written before the restore attempt, or the freshly set root would overwrite the
  // stored selection it is about to restore.
  function persist(): void {
    if (!snapshot.userId || !restoreAttempted || !snapshot.current) {
      return;
    }
    try {
      storage.setItem(
        `${SCOPE_STORAGE_PREFIX}${snapshot.userId}`,
        JSON.stringify({
          orgNodeId: snapshot.current.id,
          stackIds: snapshot.stack.map(node => node.id),
          viewMode: snapshot.viewMode,
        } satisfies PersistedScope),
      );
    } catch {
      // Storage full or unavailable: the selection just will not survive a reload.
    }
  }

  function restore(
    userId: string,
    tree: OrgNodeTree,
    base: Omit<ScopeSnapshot, 'current'>,
  ): Omit<ScopeSnapshot, 'current'> | null {
    const persisted = readPersisted(storage, userId);
    if (!persisted) {
      return null;
    }
    const byId = new Map(tree.nodes.map(node => [node.id, node]));
    const current = byId.get(persisted.orgNodeId);
    if (!current) {
      try {
        storage.removeItem(`${SCOPE_STORAGE_PREFIX}${userId}`);
      } catch {
        // Ignore: an unreadable entry is discarded on the next write anyway.
      }
      return null;
    }
    const restored = persisted.stackIds.flatMap(id => {
      const node = byId.get(id);
      return node ? [toRef(node)] : [];
    });
    const stack = restored.length === persisted.stackIds.length ? restored : [toRef(current)];
    return {
      ...base,
      stack,
      viewMode: base.canToggleViewMode ? persisted.viewMode : 'admin',
    };
  }

  function clear(): void {
    restoreAttempted = false;
    if (snapshot === EMPTY) {
      return;
    }
    snapshot = EMPTY;
    listeners.forEach(listener => listener());
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    requestScope() {
      return snapshot.current
        ? { orgNodeId: snapshot.current.id, viewMode: snapshot.viewMode }
        : null;
    },
    sync(identity, tree) {
      if (snapshot.userId !== identity.userId) {
        restoreAttempted = false;
        snapshot = { ...EMPTY, userId: identity.userId };
      }
      let next: Omit<ScopeSnapshot, 'current'> = {
        ...snapshot,
        canToggleViewMode: isGlobalAdmin(identity),
      };
      if (!next.root) {
        const root = isGlobalAdmin(identity) ? tree?.root : identity.roleContext.originNode;
        next = { ...next, root: root ? toRef(root) : null };
      }
      const restoring = Boolean(next.root && tree && !restoreAttempted);
      if (restoring && tree) {
        restoreAttempted = true;
        next = restore(identity.userId, tree, next) ?? next;
      }
      set(next);
      if (restoring) {
        // Written even when nothing changed: a stored node that could not be restored was just
        // removed, and the selection the user now has is the one to keep.
        persist();
      }
    },
    clear,
    forgetAll() {
      clear();
      removePersistedScopes(storage);
    },
    push(node) {
      set({ ...snapshot, stack: [...snapshot.stack, toRef(node)] });
    },
    pop() {
      if (snapshot.stack.length > 0) {
        set({ ...snapshot, stack: snapshot.stack.slice(0, -1) });
      }
    },
    resetTo(node) {
      set({ ...snapshot, stack: [toRef(node)] });
    },
    setViewMode(mode) {
      if (snapshot.canToggleViewMode && snapshot.viewMode !== mode) {
        set({ ...snapshot, viewMode: mode });
      }
    },
  };
}
